import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { FamilySelector } from '../components/FamilySelector';
import { Plus, CalendarCheck } from 'lucide-react';
import { useRefreshContext } from '@/contexts/RefreshContext';
import { useQueryClient } from '@tanstack/react-query';
import { useMyFamilies } from '../hooks/useMyFamilies';
import { UserStatus } from '../types/enums';

export const MyFamiliesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { registerRefresh, unregisterRefresh } = useRefreshContext();
  const queryClient = useQueryClient();
  const { families } = useMyFamilies();

  // Check if we're in the event selection flow
  const isEventFirstFlow = !!location.state?.selectedEvent;

  // Check if user has at least one active family
  const hasActiveFamily = useMemo(() => {
    return families.some((f) => f.status === UserStatus.ACTIVE);
  }, [families]);

  // Register refresh function that invalidates cache and refetches
  useEffect(() => {
    registerRefresh('/my-families', async () => {
      await queryClient.invalidateQueries({ queryKey: ['myFamilies'] });
    });
    return () => unregisterRefresh('/my-families');
  }, [registerRefresh, unregisterRefresh, queryClient]);

  const handleAddFamily = () => {
    navigate('/register');
  };

  const handleCheckInToday = () => {
    navigate('/events', { state: { todayOnly: true } });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('families.title')}</h1>
          <p className="text-gray-600">{t('families.description')}</p>
        </div>

        {/* Action buttons - only show on landing page, not when selecting for events */}
        {!isEventFirstFlow && (
          <div className="mb-6 space-y-3">
            {/* Check-in Today Button - Only show when user has at least one active family */}
            {hasActiveFamily && (
              <button
                onClick={handleCheckInToday}
                className="w-full p-4 sm:p-6 bg-[var(--color-primary-600)] hover:bg-[var(--color-primary-700)] active:bg-[var(--color-primary-800)] rounded-xl transition-all duration-200 flex items-center justify-center gap-3 sm:gap-4 group text-white shadow-md hover:shadow-lg"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CalendarCheck className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <span className="text-base sm:text-lg font-semibold text-center leading-tight">
                  {t('buttons.checkInToday')}
                </span>
              </button>
            )}

            {/* Add New Family Button - Secondary action */}
            <button
              onClick={handleAddFamily}
              className="w-full p-4 sm:p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3 group"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 group-hover:bg-blue-500 group-active:bg-blue-600 flex items-center justify-center transition-colors duration-200">
                <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 group-hover:text-white group-active:text-white transition-colors duration-200" />
              </div>
              <span className="text-base sm:text-lg font-semibold text-gray-700 group-hover:text-blue-600 group-active:text-blue-700 transition-colors duration-200">
                {t('buttons.addNewFamily')}
              </span>
            </button>
          </div>
        )}

        <FamilySelector />
      </div>
    </Layout>
  );
};
